import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import { getContentPage } from '../../data/contentPages';

const ContentLandingPage = () => {
  const { slug } = useParams();
  const page = getContentPage(slug);

  if (!page) {
    return <Navigate to="/mokymosi-gidai" replace />;
  }

  const breadcrumbs = [
    {
      name: 'Pradžia',
      path: '/',
    },
    {
      name: 'Mokymosi gidai',
      path: '/mokymosi-gidai',
    },
    {
      name: page.breadcrumbName || page.h1,
      path: page.canonical,
    },
  ];

  return (
    <>
      <SEO
        title={page.title}
        description={page.description}
        canonical={page.canonical}
        image={page.image}
        imageAlt={page.imageAlt}
        pageType="Article"
        type="article"
        breadcrumbs={breadcrumbs}
        faq={page.faq || []}
        about={page.about || []}
        learningOutcomes={page.learningOutcomes || []}
        includeWebsiteSchema={false}
      />

      <main className="sp-seo-page">
        <nav
          className="sp-seo-breadcrumbs"
          aria-label="Navigacija"
        >
          <div className="sp-seo-container">
            <Link to="/">Pradžia</Link>
            <span aria-hidden="true">→</span>
            <Link to="/mokymosi-gidai">
              Mokymosi gidai
            </Link>
            <span aria-hidden="true">→</span>
            <span>{page.breadcrumbName || page.h1}</span>
          </div>
        </nav>

        <section
          className="sp-seo-hero"
          style={{ '--sp-accent': page.accent || '#58CC02' }}
        >
          <div className="sp-seo-container">
            {page.eyebrow && (
              <div className="sp-seo-eyebrow">
                {page.eyebrow}
              </div>
            )}

            <h1>{page.h1}</h1>

            <p className="sp-seo-intro">
              {page.intro}
            </p>

            <div className="sp-seo-hero-actions">
              <Link
                to="/mokomieji-zaidimai"
                className="sp-seo-primary-btn"
              >
                Mokomieji žaidimai →
              </Link>

              <a
                href="#turinys"
                className="sp-seo-secondary-btn"
              >
                Skaityti gidą
              </a>
            </div>
          </div>
        </section>

        {page.learningOutcomes?.length > 0 && (
          <section className="sp-seo-section sp-seo-learning-outcomes">
            <div className="sp-seo-container">
              <div className="sp-seo-section-heading">
                <span>🎯</span>
                <h2>Ką sužinosite?</h2>
              </div>

              <div className="sp-seo-outcomes-grid">
                {page.learningOutcomes.map((outcome) => (
                  <div
                    className="sp-seo-outcome"
                    key={outcome}
                  >
                    <span aria-hidden="true">✓</span>
                    <span>{outcome}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {page.contentSections?.length > 0 && (
          <section
            id="turinys"
            className="sp-seo-section sp-seo-content-section"
          >
            <div className="sp-seo-container">
              {page.contentSections.map((section) => (
                <article
                  className="sp-seo-content-block"
                  key={section.heading}
                >
                  <h2>{section.heading}</h2>

                  {Array.isArray(section.paragraphs) &&
                    section.paragraphs.map(
                      (paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                      )
                    )}
                </article>
              ))}
            </div>
          </section>
        )}

        {page.games?.length > 0 && (
          <section className="sp-seo-section">
            <div className="sp-seo-container">
              <div className="sp-seo-section-heading">
                <span>🎮</span>
                <h2>{page.gamesHeading || 'Mokomieji žaidimai'}</h2>
              </div>

              <div className="sp-seo-related-grid">
                {page.games.map((game) => (
                  <Link
                    to={`/zaidimai/${game.slug}`}
                    className="sp-seo-related-card"
                    key={game.slug}
                  >
                    <strong>{game.label}</strong>
                    <span>Žaisti →</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {page.faq?.length > 0 && (
          <section className="sp-seo-section">
            <div className="sp-seo-container sp-seo-faq-container">
              <div className="sp-seo-section-heading">
                <span>❓</span>
                <h2>Dažniausiai užduodami klausimai</h2>
              </div>

              <div className="sp-seo-faq-list">
                {page.faq.map((item) => (
                  <details
                    className="sp-seo-faq"
                    key={item.question}
                  >
                    <summary>{item.question}</summary>
                    <p>{item.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {page.relatedLinks?.length > 0 && (
          <section className="sp-seo-section">
            <div className="sp-seo-container">
              <div className="sp-seo-section-heading">
                <span>🔗</span>
                <h2>Susijusi informacija</h2>
              </div>

              <div className="sp-seo-related-grid">
                {page.relatedLinks.map((link) => (
                  <Link
                    to={link.href}
                    className="sp-seo-related-card"
                    key={link.href}
                  >
                    <strong>{link.label}</strong>
                    <span>Peržiūrėti →</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="sp-seo-section">
          <div className="sp-seo-container">
            <div className="sp-seo-content-block">
              <h2>Padėkite vaikui mokytis žaidžiant</h2>
              <p>
                SmartPanda mokomieji žaidimai padeda vaikams
                praktiškai lavinti skirtingus įgūdžius interaktyvioje
                aplinkoje.
              </p>

              <Link
                to="/mokomieji-zaidimai"
                className="sp-seo-primary-btn"
              >
                Peržiūrėti mokomuosius žaidimus →
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default ContentLandingPage;
